import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  CircularProgress, Typography, Button,
} from '@mui/material';
import { getMachineGroupDetail } from '../../../../Services/app/oeeservice';
import { getMasterList, saveMasterListSafely } from '../../../../Services/app/masterservice';
import { MACHINE_HOUR_RATE_KEY, rateKey } from '../../utils/machineHourRateShared';

const BORDER = '#ECEFF3';

export default function MachineHourRateSettings({ customerId, filters, defaultRate }) {
  const [machines, setMachines] = useState([]);
  const [savedRates, setSavedRates] = useState({});
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [search, setSearch] = useState('');

  const loadAll = useCallback(async () => {
    if (!customerId || !filters) { setMachines([]); return; }
    setLoading(true);
    setError('');
    setSaveMessage('');
    try {
      const [groupDetail, rateList] = await Promise.all([
        getMachineGroupDetail(filters.machines, filters.shift, filters.startDateISO, filters.endDateISO),
        getMasterList(customerId, MACHINE_HOUR_RATE_KEY),
      ]);
      const unique = (groupDetail || [])
        .map((r) => ({ machine_name: r.machine_name }))
        .sort((a, b) => a.machine_name.localeCompare(b.machine_name));
      setMachines(unique);

      let effectiveList = rateList;
      if (rateList.length === 0 && unique.length > 0) {
        const seedRate = Number(defaultRate) || 240;
        const seeded = unique.map((row) => ({ machine_name: row.machine_name, rate_per_hour: seedRate }));
        try {
          await saveMasterListSafely({
            customerId,
            key: MACHINE_HOUR_RATE_KEY,
            mutate: () => seeded,
            skipBackup: true,
          });
          effectiveList = seeded;
        } catch (seedErr) {
          console.error('Failed to seed machine hour rate list', seedErr);
        }
      }

      const rateMap = {};
      effectiveList.forEach((e) => { rateMap[rateKey(e)] = e.rate_per_hour; });
      setSavedRates(rateMap);
      const initialInputs = {};
      unique.forEach((row) => {
        const key = rateKey(row);
        if (rateMap[key] !== undefined && rateMap[key] !== null) initialInputs[key] = String(rateMap[key]);
      });
      setInputs(initialInputs);
    } catch (err) {
      console.error('Failed to load machine hour rate settings', err);
      setError('Failed to load machine list.');
    } finally {
      setLoading(false);
    }
  }, [customerId, filters, defaultRate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleChange = (row, value) => {
    setInputs((prev) => ({ ...prev, [rateKey(row)]: value }));
  };

  const handleSave = async (effectiveInputs) => {
    const useInputs = effectiveInputs || inputs;
    setSaving(true);
    setSaveMessage('');
    try {
      const changed = machines.filter((row) => {
        const key = rateKey(row);
        const raw = useInputs[key];
        const nextVal = raw === undefined || raw === '' ? null : Number(raw);
        const prevVal = savedRates[key] ?? null;
        return nextVal !== prevVal && !(nextVal === null && prevVal === null) && !Number.isNaN(nextVal);
      });
      if (changed.length === 0) {
        setSaveMessage('Nothing to save.');
        return;
      }
      const hasDeletion = changed.some((row) => {
        const key = rateKey(row);
        const raw = useInputs[key];
        const nextVal = raw === undefined || raw === '' ? null : Number(raw);
        return nextVal === null && savedRates[key] !== undefined && savedRates[key] !== null;
      });
      await saveMasterListSafely({
        customerId,
        key: MACHINE_HOUR_RATE_KEY,
        allowShrink: hasDeletion,
        baselineCount: Object.keys(savedRates).length,
        skipBackup: true,
        mutate: (freshList) => {
          let next = freshList.slice();
          changed.forEach((row) => {
            const key = rateKey(row);
            const raw = useInputs[key];
            const nextVal = raw === undefined || raw === '' ? null : Number(raw);
            const idx = next.findIndex((e) => e.machine_name === row.machine_name);
            if (nextVal === null) {
              if (idx !== -1) next.splice(idx, 1);
              return;
            }
            const entry = { machine_name: row.machine_name, rate_per_hour: nextVal };
            if (idx === -1) next.push(entry);
            else next[idx] = entry;
          });
          return next;
        },
      });
      setSaveMessage(`Saved ${changed.length} rate${changed.length > 1 ? 's' : ''}.`);
      await loadAll();
    } catch (err) {
      console.error('Failed to save machine hour rates', err);
      setSaveMessage('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyToAllAndSave = async () => {
    if (bulkValue === '' || Number.isNaN(Number(bulkValue))) return;
    const next = {};
    machines.forEach((row) => { next[rateKey(row)] = bulkValue; });
    setInputs(next);
    await handleSave(next);
  };

  if (!filters) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select filters and click Submit on the Insights page, then reopen Settings to configure machine hourly rates.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (error) {
    return <Typography variant="body2" color="error">{error}</Typography>;
  }

  if (machines.length === 0) {
    return <Typography variant="body2" color="text.secondary">No machines found for the current filters.</Typography>;
  }

  const searchTerm = search.trim().toLowerCase();
  const filteredMachines = searchTerm
    ? machines.filter((row) => row.machine_name.toLowerCase().includes(searchTerm))
    : machines;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search machine"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 220 }}
        />
        <TextField
          size="small"
          type="number"
          label="Set all to"
          value={bulkValue}
          onChange={(e) => setBulkValue(e.target.value)}
          sx={{ width: 140 }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={handleApplyToAllAndSave}
          disabled={saving || bulkValue === '' || Number.isNaN(Number(bulkValue))}
        >
          {saving ? 'Saving...' : `Apply to all ${machines.length} & Save`}
        </Button>
      </Box>
      <Box sx={{ maxHeight: 320, overflow: 'auto', border: `1px solid ${BORDER}`, borderRadius: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, background: '#f5f5f5' }}>Machine</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12, background: '#f5f5f5' }} align="right">Rate / Hour</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMachines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ fontSize: 12, color: '#898781', py: 2 }}>
                  No matches for "{search}".
                </TableCell>
              </TableRow>
            ) : filteredMachines.map((row) => {
              const key = rateKey(row);
              return (
                <TableRow key={key}>
                  <TableCell sx={{ fontSize: 12 }}>{row.machine_name}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      placeholder={defaultRate ? `${defaultRate} (default)` : '0.00'}
                      value={inputs[key] ?? ''}
                      onChange={(e) => handleChange(row, e.target.value)}
                      onBlur={() => handleSave()}
                      inputProps={{ style: { textAlign: 'right', fontSize: 12, padding: '4px 6px' } }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, mt: 1.5 }}>
        {saveMessage && (
          <Typography variant="caption" color="text.secondary">{saveMessage}</Typography>
        )}
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Machine Hour Rates'}
        </Button>
      </Box>
    </Box>
  );
}
